import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import LinkExt from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { getClientContacts } from '../../api';
import TemplatePickerDialog from './TemplatePickerDialog';
import MergeTagInserter, { MergeTagButton } from './MergeTagInserter';
import AttachmentPicker from './AttachmentPicker';
import { useMergeTags, emailKeys } from '../../hooks/useEmails';
import {
  createEmail,
  updateEmail,
  sendEmail,
  scheduleEmail,
  uploadEmailAttachment,
  getEmailApiErrorMessage,
} from '../../api/emails';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function htmlToPlain(html) {
  if (typeof document === 'undefined') return (html || '').replace(/<[^>]*>/g, '').trim();
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return (d.textContent || '').trim();
}

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (action, label, activeCheck) => (
    <Button
      key={label}
      size="small"
      onMouseDown={(e) => {
        e.preventDefault();
        action();
      }}
      sx={{
        minWidth: 28,
        px: 0.5,
        fontWeight: 700,
        fontSize: 11,
        color: activeCheck ? 'primary.main' : 'text.secondary',
        bgcolor: activeCheck ? 'action.selected' : 'transparent',
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        flexWrap: 'wrap',
        p: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      {btn(() => editor.chain().focus().toggleBold().run(), 'B', editor.isActive('bold'))}
      {btn(() => editor.chain().focus().toggleItalic().run(), 'I', editor.isActive('italic'))}
      {btn(() => editor.chain().focus().toggleUnderline().run(), 'U', editor.isActive('underline'))}
      {btn(() => editor.chain().focus().toggleBulletList().run(), '•', editor.isActive('bulletList'))}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), '1.', editor.isActive('orderedList'))}
    </Box>
  );
}

const buildSchema = () =>
  z
    .object({
      to: z.array(z.string()),
      cc: z.array(z.string()),
      bcc: z.array(z.string()),
      subject: z.string(),
      schedule: z.boolean(),
      scheduledAt: z.any().nullable(),
    })
    .superRefine((val, ctx) => {
      if (!val.to?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Add at least one recipient', path: ['to'] });
      }
      (val.to || []).forEach((em) => {
        if (em && !EMAIL_REGEX.test(em)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid email: ${em}`, path: ['to'] });
        }
      });
      (val.cc || []).forEach((em) => {
        if (em && !EMAIL_REGEX.test(em)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid email: ${em}`, path: ['cc'] });
        }
      });
      (val.bcc || []).forEach((em) => {
        if (em && !EMAIL_REGEX.test(em)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid email: ${em}`, path: ['bcc'] });
        }
      });
      if (!val.subject?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Subject is required', path: ['subject'] });
      }
      if (val.schedule) {
        if (!val.scheduledAt || !dayjs(val.scheduledAt).isValid()) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Pick a schedule time', path: ['scheduledAt'] });
        } else if (dayjs(val.scheduledAt).isBefore(dayjs())) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Schedule time must be in the future', path: ['scheduledAt'] });
        }
      }
    });

export default function ComposeEmailDialog({
  open,
  onClose,
  clientId: clientIdProp,
  initialClientId,
  threadId,
  defaultTo,
  onSuccess,
  onSent,
}) {
  const clientId = clientIdProp ?? initialClientId ?? null;
  const notifySuccess = onSuccess ?? onSent;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { data: mergeTags = [] } = useMergeTags({ enabled: open });

  const [fromLabel, setFromLabel] = useState('');
  const [contactOptions, setContactOptions] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [mergeAnchor, setMergeAnchor] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [pendingAttach, setPendingAttach] = useState([]);
  const [topError, setTopError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bodyError, setBodyError] = useState(null);

  const schema = useMemo(() => buildSchema(), []);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      to: [],
      cc: [],
      bcc: [],
      subject: '',
      schedule: false,
      scheduledAt: null,
    },
  });

  const scheduleEnabled = watch('schedule');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      LinkExt.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write your message…' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        tabIndex: 0,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const t = htmlToPlain(ed.getHTML());
      if (t) setBodyError(null);
    },
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const em = u.email || u.username || '';
        setFromLabel(em ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || em : 'Current user');
      } else {
        setFromLabel('Current user');
      }
    } catch {
      setFromLabel('Current user');
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientId) {
      setContactOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getClientContacts(clientId);
        const raw = res?.data;
        const rows = Array.isArray(raw) ? raw : raw?.results || [];
        const opts = rows
          .map((r) => r.email || r.contact_email || r.primary_email)
          .filter(Boolean);
        if (!cancelled) setContactOptions(opts);
      } catch {
        if (!cancelled) setContactOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  useEffect(() => {
    if (!open) return;
    reset({
      to: defaultTo ? [defaultTo] : [],
      cc: [],
      bcc: [],
      subject: '',
      schedule: false,
      scheduledAt: null,
    });
    setShowCc(false);
    setShowBcc(false);
    setDraftId(null);
    setPendingAttach([]);
    setTopError(null);
    setBodyError(null);
    editor?.commands.setContent('');
  }, [open, defaultTo, reset, editor]);

  const insertMerge = useCallback(
    (tag) => {
      editor?.chain().focus().insertContent(tag).run();
    },
    [editor]
  );

  const buildPayload = (vals, html) => {
    const p = {
      subject: vals.subject.trim(),
      body_html: html,
      to_addresses: (vals.to || []).map((s) => String(s).trim()).filter(Boolean),
      cc_addresses: (vals.cc || []).map((s) => String(s).trim()).filter(Boolean),
      bcc_addresses: (vals.bcc || []).map((s) => String(s).trim()).filter(Boolean),
    };
    if (clientId) p.client_id = clientId;
    if (threadId) p.email_thread_id = threadId;
    return p;
  };

  const persistDraft = async (vals, html) => {
    const payload = buildPayload(vals, html);
    if (draftId) {
      const data = await updateEmail(draftId, payload);
      return { ...data, id: draftId };
    }
    const data = await createEmail(payload);
    setDraftId(data.id);
    return data;
  };

  const flushAttachments = async (emailId) => {
    for (const item of pendingAttach) {
      if (item.file) {
        const fd = new FormData();
        fd.append('file', item.file);
        await uploadEmailAttachment(emailId, fd);
      } else if (item.existing_document_id) {
        const fd = new FormData();
        fd.append('existing_document_id', item.existing_document_id);
        await uploadEmailAttachment(emailId, fd);
      }
    }
  };

  const onSubmitInternal = async (vals, mode) => {
    const html = editor?.getHTML() || '';
    const plain = htmlToPlain(html);
    if (!plain) {
      setBodyError('Message body is required');
      return;
    }
    setBodyError(null);
    setTopError(null);
    if (mode === 'draft') {
      setSaving(true);
      try {
        const saved = await persistDraft(vals, html);
        await flushAttachments(saved.id);
        setPendingAttach([]);
        setSnack({ severity: 'success', message: 'Draft saved.' });
        queryClient.invalidateQueries({ queryKey: emailKeys.all });
        notifySuccess?.({ type: 'draft', clientId });
      } catch (e) {
        setTopError(getEmailApiErrorMessage(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const saved = await persistDraft(vals, html);
      const emailId = saved.id;
      await flushAttachments(emailId);
      setPendingAttach([]);

      if (vals.schedule && vals.scheduledAt) {
        const iso = dayjs(vals.scheduledAt).toISOString();
        await scheduleEmail(emailId, iso);
        const firstTo = vals.to[0] || 'recipient';
        setSnack({ severity: 'success', message: `Email scheduled for ${firstTo}` });
      } else {
        await sendEmail(emailId);
        const toStr = vals.to.join(', ');
        setSnack({ severity: 'success', message: `Email sent to ${toStr}` });
      }
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: ['client', clientId, 'timeline'] });
      }
      notifySuccess?.({ type: 'sent', clientId, recipients: vals.to });
      onClose();
    } catch (e) {
      setTopError(getEmailApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTemplate = (tpl) => {
    setValue('subject', tpl.subject_template || '', { shouldValidate: true });
    const body = tpl.body_template || '';
    editor?.commands.setContent(body);
  };

  const primaryActionLabel = scheduleEnabled ? 'Schedule' : 'Send';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Typography component="span" variant="h6" fontWeight={700}>
            New Email
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {topError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTopError(null)}>
              {topError}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            From
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={fromLabel}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            To
          </Typography>
          <Controller
            name="to"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                freeSolo
                options={contactOptions}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Recipients"
                    error={Boolean(errors.to)}
                    helperText={errors.to?.message}
                  />
                )}
              />
            )}
          />

          {!showCc && (
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setShowCc(true)}
              sx={{ mt: 1, display: 'inline-block' }}
            >
              Add Cc
            </Link>
          )}
          {!showBcc && (
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setShowBcc(true)}
              sx={{ mt: 1, ml: showCc ? 2 : 0, display: 'inline-block' }}
            >
              Add Bcc
            </Link>
          )}

          {showCc && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Cc
              </Typography>
              <Controller
                name="cc"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value}
                    onChange={(_, v) => field.onChange(v)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`cc-${option}-${index}`} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Cc" error={Boolean(errors.cc)} helperText={errors.cc?.message} />
                    )}
                  />
                )}
              />
            </Box>
          )}

          {showBcc && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Bcc
              </Typography>
              <Controller
                name="bcc"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value}
                    onChange={(_, v) => field.onChange(v)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`bcc-${option}-${index}`} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Bcc"
                        error={Boolean(errors.bcc)}
                        helperText={errors.bcc?.message}
                      />
                    )}
                  />
                )}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Subject
            </Typography>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  error={Boolean(errors.subject)}
                  helperText={errors.subject?.message}
                  inputProps={{ tabIndex: 0 }}
                />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2, mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTemplateOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Start from template…
            </Button>
            <MergeTagButton onClick={(e) => setMergeAnchor(e.currentTarget)} disabled={!editor} />
            <Button size="small" variant="outlined" onClick={() => setAttachOpen(true)} sx={{ textTransform: 'none' }}>
              Attach files…
            </Button>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              minHeight: 220,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <EditorToolbar editor={editor} />
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 1.5,
                '& .ProseMirror': {
                  outline: 'none',
                  minHeight: 160,
                  fontSize: 14,
                  lineHeight: 1.6,
                },
              }}
            >
              <EditorContent editor={editor} />
            </Box>
          </Box>
          {bodyError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {bodyError}
            </Typography>
          )}

          {pendingAttach.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {pendingAttach.map((a, i) => (
                <Chip
                  key={`${a.filename}-${i}`}
                  label={a.filename}
                  size="small"
                  onDelete={() => setPendingAttach((p) => p.filter((_, j) => j !== i))}
                />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Controller
              name="schedule"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Schedule send"
                />
              )}
            />
            {scheduleEnabled && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="scheduledAt"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Send at"
                      value={field.value}
                      onChange={field.onChange}
                      minDateTime={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: Boolean(errors.scheduledAt),
                          helperText: errors.scheduledAt?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={handleSubmit((vals) => onSubmitInternal(vals, 'draft'))}
            disabled={saving || submitting}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ textTransform: 'none' }}
          >
            Save Draft
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSubmit((vals) => onSubmitInternal(vals, 'send'))}
            disabled={saving || submitting}
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : scheduleEnabled ? <ScheduleIcon /> : <SendIcon />
            }
            sx={{ textTransform: 'none' }}
          >
            {submitting ? 'Working…' : primaryActionLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplatePickerDialog open={templateOpen} onClose={() => setTemplateOpen(false)} onSelect={handleTemplate} />

      <AttachmentPicker
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        clientId={clientId}
        onConfirm={(items) => {
          setPendingAttach((p) => [...p, ...items]);
        }}
      />

      <MergeTagInserter
        anchorEl={mergeAnchor}
        open={Boolean(mergeAnchor)}
        onClose={() => setMergeAnchor(null)}
        mergeTags={mergeTags}
        onInsert={insertMerge}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}
